// Výkaz práce jako stažitelné PDF. Sestavuje se přes pdfmake (vektorový text,
// diakritika sedí — Roboto z pdfmake má českou sadu ověřenou). Knihovna se
// načítá až při kliknutí, aby nezvětšovala hlavní bundle.

import type { VykazData, VykazRow } from '@/components/vyroba/types';

type Content = Record<string, unknown>;

/** Sekundy → „1 hod, 43 min 46 sec". */
function fmtDur(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return h > 0 ? `${h} hod, ${m} min ${s} sec` : `${m} min ${s} sec`;
}

/** UTC z Tabidoo → „04.05.2026 06:04:34" v našem čase. */
function fmtDateTime(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

/** Podbarvení buňky s výkonem — stejné hranice jako v přehledu. */
function perfFill(pct: number): string {
  if (pct >= 75) return '#d1fae5';
  if (pct >= 50) return '#fef3c7';
  return '#fee2e2';
}

/** Kusy po kategoriích v pořadí, jak se objevily v zakázkách. */
function categories(rows: VykazRow[]): { category: string; qty: number }[] {
  const map = new Map<string, number>();
  for (const r of rows) map.set(r.category || '—', (map.get(r.category || '—') ?? 0) + r.total_qty);
  return [...map].map(([category, qty]) => ({ category, qty }));
}

/** Kusy per velikost uvnitř kategorie — podklad pro sloupcové grafy. */
function sizesByCategory(rows: VykazRow[]): [string, { label: string; qty: number }[]][] {
  const out = new Map<string, Map<string, number>>();
  for (const r of rows) {
    const cat = r.category || '—';
    const inner = out.get(cat) ?? new Map<string, number>();
    for (const s of r.sizes) inner.set(s.label, (inner.get(s.label) ?? 0) + s.qty);
    out.set(cat, inner);
  }
  return [...out].map(([cat, inner]) => [
    cat,
    [...inner]
      .map(([label, qty]) => ({ label, qty }))
      .sort((a, b) => {
        const na = Number.parseInt(a.label, 10);
        const nb = Number.parseInt(b.label, 10);
        if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
        return a.label.localeCompare(b.label, 'cs');
      }),
  ]);
}

function escapeXml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Sloupcový graf jako SVG string — pdfmake ho vloží vektorově. */
function barChartSvg(title: string, data: { label: string; qty: number }[]): string {
  const W = 640;
  const H = 260;
  const padL = 40;
  const padB = 34;
  const padT = 30;
  const max = Math.max(1, ...data.map((d) => d.qty));
  const step = Math.max(1, Math.ceil(max / 5));
  const top = Math.ceil(max / step) * step;
  const ticks = Array.from({ length: top / step + 1 }, (_, i) => i * step);
  const bw = (W - padL - 10) / Math.max(1, data.length);
  const parts: string[] = [];

  parts.push(
    `<text x="${W / 2}" y="16" font-size="12" text-anchor="middle" fill="#374151">${escapeXml(title)}</text>`,
  );
  for (const t of ticks) {
    const y = H - padB - ((H - padB - padT) * t) / top;
    parts.push(
      `<line x1="${padL}" y1="${y}" x2="${W - 10}" y2="${y}" stroke="#e5e7eb" stroke-width="1"/>`,
      `<text x="${padL - 6}" y="${y + 3}" font-size="9" text-anchor="end" fill="#6b7280">${t}</text>`,
    );
  }
  data.forEach((d, i) => {
    const h = ((H - padB - padT) * d.qty) / top;
    const x = padL + i * bw + bw * 0.2;
    const y = H - padB - h;
    if (d.qty > 0) {
      parts.push(
        `<rect x="${x}" y="${y}" width="${bw * 0.6}" height="${h}" fill="#7ec8f0"/>`,
        `<text x="${x + bw * 0.3}" y="${y - 4}" font-size="9" font-weight="bold" text-anchor="middle" fill="#374151">${d.qty}</text>`,
      );
    }
    parts.push(
      `<text x="${x + bw * 0.3}" y="${H - padB + 14}" font-size="9" text-anchor="middle" fill="#6b7280">${escapeXml(d.label)}</text>`,
    );
  });
  parts.push(
    `<line x1="${padL}" y1="${H - padB}" x2="${W - 10}" y2="${H - padB}" stroke="#9ca3af" stroke-width="1"/>`,
    `<text x="${W / 2}" y="${H - 6}" font-size="9" text-anchor="middle" fill="#6b7280">Velikosti</text>`,
  );
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">${parts.join('')}</svg>`;
}

/** HTML z editoru poznámek → odstavce a seznamy pro pdfmake. */
function noteToContent(html: string): Content[] {
  if (!html.trim()) return [];
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');

  const runs = (node: Node, bold = false, italics = false, underline = false): Content[] => {
    const out: Content[] = [];
    for (const child of node.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent ?? '';
        if (text)
          out.push({ text, bold, italics, decoration: underline ? 'underline' : undefined });
        continue;
      }
      if (!(child instanceof Element)) continue;
      const tag = child.tagName;
      if (tag === 'BR') {
        out.push({ text: '\n' });
        continue;
      }
      out.push(
        ...runs(
          child,
          bold || tag === 'B' || tag === 'STRONG',
          italics || tag === 'I' || tag === 'EM',
          underline || tag === 'U',
        ),
      );
    }
    return out;
  };

  const blocks: Content[] = [];
  for (const el of doc.body.children) {
    if (el.tagName === 'UL' || el.tagName === 'OL') {
      const items = [...el.children].map((li) => ({ text: runs(li) }));
      blocks.push(el.tagName === 'UL' ? { ul: items } : { ol: items });
      continue;
    }
    const align = el.getAttribute('style')?.match(/text-align:\s*(\w+)/)?.[1];
    blocks.push({ text: runs(el), alignment: align, margin: [0, 0, 0, 2] });
  }
  // Poznámka bez značek (starší záznamy) — ať se aspoň zachová odřádkování.
  if (blocks.length === 0) blocks.push({ text: doc.body.textContent ?? '' });
  return blocks;
}

/** Stáhne výkaz jako PDF. Název souboru: „vykaz-05-2026-Lenka.pdf". */
export async function downloadVykazPdf(opts: {
  data: VykazData;
  note: string;
  errors: number;
  svadlenka: string;
  month: number;
  year: number;
}): Promise<void> {
  const { data, note, errors, svadlenka, month, year } = opts;
  const [pdfMakeMod, vfsMod] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/vfs_fonts'),
  ]);
  const pdfMake = ((pdfMakeMod as unknown as { default?: PdfMake }).default ??
    pdfMakeMod) as PdfMake;
  const vfs = ((vfsMod as unknown as { default?: Record<string, string> }).default ??
    vfsMod) as Record<string, string>;
  // pdfmake 0.2.2x už nemá `.vfs` — fonty se předávají tímhle voláním.
  pdfMake.addVirtualFileSystem(vfs);

  const errorRate = data.summary.total_qty > 0 ? (errors / data.summary.total_qty) * 100 : 0;
  const info: Content[] = [
    { text: [{ text: 'Jméno: ', bold: true }, svadlenka] },
    { text: [{ text: 'Počet zakázek: ', bold: true }, String(data.summary.orders)] },
    {
      text: [
        { text: 'Předpokládaný čas šití celkem: ', bold: true },
        `${data.summary.norm_hours.toFixed(1)}h`,
      ],
    },
    {
      text: [{ text: 'Čas šití celkem: ', bold: true }, `${data.summary.sewing_hours.toFixed(1)}h`],
    },
    { text: [{ text: 'Celkový počet kusů: ', bold: true }, String(data.summary.total_qty)] },
    { text: [{ text: 'Počet chyb: ', bold: true }, String(errors)] },
    { text: [{ text: 'Chybovost: ', bold: true }, `${errorRate.toFixed(1)}%`] },
  ];

  const header = [
    'ID zakázky',
    'Kód produktu',
    'Celkem ks',
    'Švadlenka',
    'Start šití',
    'Konec šití',
    'Předpokládaný čas šití',
    'Doba šití',
    'Výkon',
  ].map((text) => ({ text, bold: true, fillColor: '#eeeeee', alignment: 'center' }));

  const body = [
    header,
    ...data.rows.map((r) => [
      { text: r.id_zakazky, alignment: 'center' },
      { text: r.product_code, alignment: 'center' },
      { text: String(r.total_qty), alignment: 'center' },
      { text: r.svadlenka, alignment: 'center' },
      { text: fmtDateTime(r.start), alignment: 'center' },
      { text: fmtDateTime(r.konec), alignment: 'center' },
      { text: r.norm_text, alignment: 'center' },
      { text: fmtDur(r.duration_sec), alignment: 'center' },
      {
        text: `${r.performance.toFixed(2)}%`,
        alignment: 'center',
        fillColor: perfFill(r.performance),
      },
    ]),
  ];

  const cats = categories(data.rows);
  const charts = sizesByCategory(data.rows).filter(([, list]) => list.length > 0);

  const content: Content[] = [
    { text: 'VÝKAZ PRÁCE', fontSize: 20, bold: true, alignment: 'center', margin: [0, 0, 0, 6] },
    {
      text: `${String(month).padStart(2, '0')}/${year}`,
      fontSize: 14,
      bold: true,
      alignment: 'center',
      margin: [0, 0, 0, 18],
    },
    {
      columns: [
        {
          width: '50%',
          stack: [
            { text: 'Informace', fontSize: 12, bold: true, margin: [0, 0, 0, 6] },
            ...info.map((i) => ({ ...i, fontSize: 10, margin: [0, 0, 0, 2] })),
          ],
        },
        {
          width: '50%',
          stack: [
            { text: 'Poznámky', fontSize: 12, bold: true, margin: [0, 0, 0, 6] },
            ...noteToContent(note).map((n) => ({ ...n, fontSize: 10 })),
          ],
        },
      ],
      columnGap: 20,
      margin: [0, 0, 0, 16],
    },
    {
      table: { headerRows: 1, widths: Array(9).fill('auto'), body },
      layout: {
        hLineColor: () => '#999999',
        vLineColor: () => '#999999',
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
      },
      fontSize: 7,
    },
    {
      text: 'VÝKAZ DLE KATEGORIÍ',
      fontSize: 18,
      bold: true,
      alignment: 'center',
      pageBreak: 'before',
      margin: [0, 0, 0, 20],
    },
    {
      table: {
        headerRows: 1,
        widths: ['*', 60],
        body: [
          [
            { text: 'Kategorie', bold: true, fillColor: '#eeeeee' },
            { text: 'Celkem', bold: true, fillColor: '#eeeeee', alignment: 'center' },
          ],
          ...cats.map((c) => [c.category, { text: String(c.qty), alignment: 'center' }]),
          [
            { text: 'Celkem', bold: true },
            { text: String(data.summary.total_qty), bold: true, alignment: 'center' },
          ],
        ],
      },
      layout: {
        hLineColor: () => '#999999',
        vLineColor: () => '#999999',
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
      },
      fontSize: 9,
    },
  ];

  charts.forEach(([category, list], i) => {
    if (i === 0) {
      content.push({
        text: 'PŘEHLED VELIKOSTÍ PODLE KATEGORIÍ',
        fontSize: 18,
        bold: true,
        alignment: 'center',
        pageBreak: 'before',
        margin: [0, 0, 0, 16],
      });
    } else if (i % 2 === 0) {
      content.push({ text: '', pageBreak: 'before' });
    }
    content.push({ svg: barChartSvg(category, list), width: 500, margin: [0, 0, 0, 18] });
  });

  const file = `vykaz-${String(month).padStart(2, '0')}-${year}-${svadlenka.replace(/\s+/g, '-')}.pdf`;
  pdfMake
    .createPdf({
      pageSize: 'A4',
      pageMargins: [34, 34, 34, 34],
      defaultStyle: { font: 'Roboto', fontSize: 9 },
      content,
    })
    .download(file);
}

/** Minimální tvar pdfmake, který používáme (knihovna vlastní typy nenabízí v ESM). */
type PdfMake = {
  addVirtualFileSystem: (vfs: Record<string, string>) => void;
  createPdf: (def: Record<string, unknown>) => { download: (name: string) => void };
};
